/**
 * Building and opening save snapshots (R10) — the pure half of §12.
 *
 * @remarks
 * `engine-runtime/saves` is pure: build, verify, migrate. WHERE a snapshot
 * lives (IndexedDB, a file, a cloud) is the adapter's business, and WHEN one
 * is written is the store listener's — this module only ever turns a state
 * into an envelope and an envelope back into a state, deterministically.
 *
 * The invariant everything here serves (§12): **save → load → behaviorally
 * identical future**, pending timed events and PRNG draws included. D23 made
 * the committed `RuntimeState` the whole snapshot, so building is mostly
 * envelope work: canonical-JSON checksum, version stamps, and the transcript
 * policy applied honestly.
 *
 * Opening is result-typed and refuses loudly, in order: malformed shape
 * (schema), wrong game, wrong pack version (exact-match pre-1.0, the
 * loader's own policy), checksum mismatch (a torn write), and an engine
 * schema this build cannot migrate. The MIGRATION REGISTRY is a pure ordered
 * walk of `(olderSnapshot) → newerSnapshot` steps keyed by the version they
 * migrate FROM; it ships empty at v1, and the walking machinery is tested
 * with a synthetic step so the first real migration lands as data.
 *
 * @packageDocumentation
 */

import {
	type RuntimeState,
	runtimeStateSchema,
	type SaveSnapshot,
	type SaveSnapshotEnvelope,
	saveSnapshotEnvelopeSchema,
	type TranscriptPolicy,
} from '@orpheus/content-schema';

import type { EngineGameBundle } from '../bundle/engine-game-bundle.js';

import { fnv1a64, toCanonicalJson } from './canonical-json.js';
import { SAVE_MIGRATIONS, type SaveMigration } from './save-migrations.js';

// The registry and its steps live beside this module since D231 (the file-size cap); both still resolve from here.
export { SAVE_MIGRATIONS, type SaveMigration } from './save-migrations.js';

/**
 * The engine's own snapshot-schema version — the migration registry's key.
 *
 * @remarks
 * Independent of the CONTENT schema version on purpose: a pack format change
 * and a runtime-state format change are different events with different
 * migration stories.
 *
 * History: `'1'` R10 genesis; `'2'` R11 added `narrative.firedTriggerIds`;
 * `'3'` R12 replaced `narrative.firedVoiceLineIds` with `voiceLineUsage` and
 * added `occurrenceTallies` + `voiceLineLastSpokenTurn`; `'4'` R17/D79
 * replaced `session.pendingCompletion` with the `pendingQuestion` union;
 * `'5'` R25 added the acquisition record and dropped-item locations; `'6'`
 * D138 added `world.revealedExitIds`; `'7'` D141 added
 * `randomness.sessionSeed`; `'8'` D143 added `world.equippedItemIds`; `'9'`
 * D151 turned `narrative.selectedEndingId` into the `selectedEndingIds`
 * history; `'10'` D152 added `world.spentUses`; `'11'` D231 added
 * `narrative.spentTopicIds`; `'12'` D232 added `world.actorLocations`;
 * `'13'` D234 added `world.actorHealth` and `world.attackedActorIds` (and
 * the optional `world.playerHealth`, which a whole player never carries);
 * `'14'` D235 added `narrative.askedPromptIds` (the `dialogue` question kind
 * joined the `pendingQuestion` union without a bump — an older save simply
 * has none parked); `'15'` D240 added `world.searchedActorIds`; `'16'` D241
 * added the `record` sub-state, the run's view of the Record; `'17'` D244
 * added `world.counterValues`, the amounts the world holds.
 */
export const ENGINE_SCHEMA_VERSION = '17';

/**
 * Where a save came from, and where it will not go (D164): the published
 * bundle it was played on, and a newer one the player declined. Both are
 * envelope facts the game logic never reads — the host reads them on the
 * next open to decide whether the session follows the game (D163).
 */
export interface SaveOrigin {
	readonly bundleHash?: string;
	readonly declinedBundleHash?: string;
}

/** Everything the pure builder cannot know for itself. */
export interface BuildSaveSnapshotOptions {
	/** The published bundle's hash and any declined one; absent for a pack that was never published under a hash. */
	readonly origin?: SaveOrigin;
	/** Display metadata only — the pure core reads no clock, so the caller supplies it. */
	readonly savedAtIso: string;
	/** Defaults to `full`; a truncated policy keeps the transcript's LAST entries. */
	readonly transcriptPolicy?: TranscriptPolicy;
}

/**
 * Build one snapshot from a committed state.
 *
 * @param bundle - The loaded bundle (identity stamps)
 * @param state - The fully-committed state (D23: the whole future)
 * @param options - Clock metadata and transcript policy
 * @returns The snapshot, checksummed over its (possibly truncated) state
 */
export const buildSaveSnapshot = (
	bundle: EngineGameBundle,
	state: RuntimeState,
	options: BuildSaveSnapshotOptions,
): SaveSnapshot => {
	const transcriptPolicy = options.transcriptPolicy ?? { type: 'full' };
	const runtimeState: RuntimeState =
		transcriptPolicy.type === 'full'
			? state
			: {
				...state,
				session: {
					...state.session,
					transcript: state.session.transcript.slice(-transcriptPolicy.keepLastEntries),
				},
			};

	const { bundleHash, declinedBundleHash } = options.origin ?? {};

	return {
		...(bundleHash === undefined ? {} : { bundleHash }),
		bundleId: bundle.contentPack.manifest.gameId,
		bundleVersion: bundle.contentPack.manifest.schemaVersion,
		checksum: fnv1a64(toCanonicalJson(runtimeState)),
		...(declinedBundleHash === undefined ? {} : { declinedBundleHash }),
		engineSchemaVersion: ENGINE_SCHEMA_VERSION,
		runtimeState,
		savedAtIso: options.savedAtIso,
		transcriptPolicy,
	};
};

/**
 * Read a snapshot's origin without opening it (D164): the envelope alone,
 * unmigrated — so a host can decide WHICH bundle to open the save against
 * before it opens it. `undefined` when the value is not a snapshot at all;
 * an empty origin when it is one that was never published under a hash.
 *
 * @param rawSnapshot - Whatever the adapter read back
 * @returns The origin, or nothing for a value that is not a snapshot
 */
export const readSaveOrigin = (rawSnapshot: unknown): SaveOrigin | undefined => {
	const parsed = saveSnapshotEnvelopeSchema.safeParse(rawSnapshot);

	if (!parsed.success) {
		return undefined;
	}

	const { bundleHash, declinedBundleHash } = parsed.data;

	return { ...(bundleHash === undefined ? {} : { bundleHash }), ...(declinedBundleHash === undefined ? {} : { declinedBundleHash }) };
};

/** Why a snapshot refused to open — every arm names what the player/adapter can do about it. */
export type OpenSaveSnapshotFailure =
	| { readonly detail: string; readonly type: 'malformed_snapshot' }
	| { readonly expected: string; readonly received: string; readonly type: 'checksum_mismatch' }
	| { readonly expected: string; readonly received: string; readonly type: 'wrong_bundle_version' }
	| { readonly expected: string; readonly received: string; readonly type: 'wrong_bundle' }
	| { readonly received: string; readonly type: 'unmigratable_engine_schema' };

/** The result of opening: the state to resume, or the typed refusal. */
export type OpenSaveSnapshotOutcome =
	| { readonly failure: OpenSaveSnapshotFailure; readonly type: 'refused' }
	| { readonly state: RuntimeState; readonly type: 'opened' };

/**
 * Validate, verify, migrate, and open one snapshot against a loaded bundle.
 *
 * @param rawSnapshot - Whatever the adapter read back (unknown-shaped on purpose)
 * @param bundle - The bundle the session will resume into
 * @returns The state to resume, or the typed refusal
 */
export const openSaveSnapshot = (rawSnapshot: unknown, bundle: EngineGameBundle): OpenSaveSnapshotOutcome => {
	// Envelope FIRST, state opaque: an older state's shape is what the
	// migration walk exists to fix, so it cannot be schema-checked yet.
	const parsedEnvelope = saveSnapshotEnvelopeSchema.safeParse(rawSnapshot);

	if (!parsedEnvelope.success) {
		/*
		 * UNREACHABLE `?.`/`??`, twice over — see the sibling at the state parse.
		 *
		 * Zod's `safeParse` returns `success: false` IF AND ONLY IF `issues.length`
		 * is non-zero, and `error.issues` is a 1:1 map of that same non-empty array,
		 * so inside a `!success` branch `issues[0]` is always present. Its `message`
		 * is always a string too: `finalizeIssue` bottoms out at the literal
		 * "Invalid input" (a custom error returning '' yields an empty string, which
		 * is falsy but not nullish, so `??` still does not fire).
		 *
		 * The guard is a TYPE-level obligation, not a runtime path:
		 * `noUncheckedIndexedAccess` types `issues[0]` as possibly-undefined.
		 *
		 * Verified 2026-08-23 against zod 4.4.3, by source reading and by pushing
		 * seventeen malformed inputs (undefined, null, primitives, empty object,
		 * null-prototype, a Proxy with empty ownKeys, Symbol, function, Map, NaN,
		 * near-miss envelopes) through both schemas: 34 failures, minimum
		 * issues.length 1, zero undefined messages.
		 */
		/* v8 ignore next */
		return refused({ detail: parsedEnvelope.error.issues[0]?.message ?? 'invalid shape', type: 'malformed_snapshot' });
	}

	const migrated = walkMigrations(parsedEnvelope.data);

	if (migrated.engineSchemaVersion !== ENGINE_SCHEMA_VERSION) {
		return refused({ received: migrated.engineSchemaVersion, type: 'unmigratable_engine_schema' });
	}

	// NOW the migrated state answers to the full current schema.
	const parsedState = runtimeStateSchema.safeParse(migrated.runtimeState);

	if (!parsedState.success) {
		/* The envelope's twin — same zod guarantee, same type-level obligation. */
		/* v8 ignore next */
		return refused({ detail: parsedState.error.issues[0]?.message ?? 'invalid state', type: 'malformed_snapshot' });
	}

	const expectedBundleId = bundle.contentPack.manifest.gameId;

	if (migrated.bundleId !== expectedBundleId) {
		return refused({ expected: expectedBundleId, received: migrated.bundleId, type: 'wrong_bundle' });
	}

	const expectedBundleVersion = bundle.contentPack.manifest.schemaVersion;

	if (migrated.bundleVersion !== expectedBundleVersion) {
		return refused({ expected: expectedBundleVersion, received: migrated.bundleVersion, type: 'wrong_bundle_version' });
	}

	const recomputedChecksum = fnv1a64(toCanonicalJson(parsedState.data));

	if (recomputedChecksum !== migrated.checksum) {
		return refused({ expected: migrated.checksum, received: recomputedChecksum, type: 'checksum_mismatch' });
	}

	return { state: parsedState.data, type: 'opened' };
};

/**
 * Walk the migration registry from the snapshot's version toward the current
 * one, applying each matching step at most once.
 *
 * @param snapshot - The parsed snapshot as read
 * @returns The snapshot after every applicable step (unchanged when none apply)
 */
export const walkMigrations = (
	snapshot: SaveSnapshotEnvelope,
	migrations: readonly SaveMigration[] = SAVE_MIGRATIONS,
): SaveSnapshotEnvelope => {
	let workingSnapshot = snapshot;

	for (const migration of migrations) {
		if (migration.fromVersion === workingSnapshot.engineSchemaVersion) {
			workingSnapshot = migration.migrate(workingSnapshot);
		}
	}

	return workingSnapshot;
};

/**
 * Wrap a refusal.
 *
 * @param failure - The typed failure
 * @returns The refused outcome
 */
const refused = (failure: OpenSaveSnapshotFailure): OpenSaveSnapshotOutcome => ({ failure, type: 'refused' });
